import { useState } from "react";
import { useConnection } from "wagmi";
import { Link } from "../components/Link";
import { Dot } from "../components/ui";
import { ConnectButton } from "../wallet/ConnectButton";
import { useAccount, useStreamStatus } from "../state/account";
import { useInstruments, useMark } from "../state/market";
import { useValuations } from "../state/valuation";
import { price, usd } from "../lib/num";
import { setPref, usePref } from "../state/prefs";
import { tableHref, type Route } from "../router";
import type { Instrument } from "../sdk";
import { AccountSheet } from "./AccountSheet";

export const NAV: { view: Route["view"]; label: string; href: string }[] = [
  { view: "table", label: "Play", href: "/" },
  { view: "bets", label: "My bets", href: "/bets" },
  { view: "wallet", label: "Wallet", href: "/wallet" },
];

export function Wordmark() {
  return (
    <Link className="wordmark" href="/" aria-label="hypa home">
      hypa<span className="wordmark-dot" aria-hidden />
    </Link>
  );
}

/** The table's sound, on or off. */
function SoundToggle() {
  const on = usePref("sound");
  return (
    <button type="button" className="sound-btn" onClick={() => setPref("sound", !on)} aria-label={on ? "Mute the table" : "Unmute the table"} aria-pressed={on}>
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M3 7h3l4-3v10l-4-3H3z" />
        {on ? <path d="M13 6.5a3.5 3.5 0 0 1 0 5M14.8 4.5a6.4 6.4 0 0 1 0 9" /> : <path d="M13 7l4 4M17 7l-4 4" />}
      </svg>
    </button>
  );
}

/** The player's money, as the cage shows it, and the way to add more. */
function Cage() {
  const { address, status } = useConnection();
  const stream = useStreamStatus();
  const valued = useValuations();
  const account = useAccount();
  const [open, setOpen] = useState(false);
  if (status !== "connected" || !address) return <ConnectButton size="sm" />;
  const live = stream === "live";
  return (
    <div className="cage">
      <button type="button" className="cage-balance" onClick={() => setOpen(true)} aria-label="Account">
        <Dot tone={live ? "up" : stream === "off" ? "off" : "warn"} />
        <span className="num">{valued ? usd(valued.equity) : account ? usd(account.balance) : "—"}</span>
      </button>
      <Link href="/wallet" className="cage-add" aria-label="Deposit">
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
          <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </Link>
      <AccountSheet open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

const GLYPH: Record<string, string> = { BTC: "₿", ETH: "Ξ" };

function TableTab({ instrument, current, riding }: { instrument: Instrument; current: boolean; riding: boolean }) {
  const mark = useMark(instrument.id);
  return (
    <Link href={tableHref(instrument.symbol)} className="ttab" aria-current={current ? "page" : undefined} aria-label={`${instrument.symbol} table`}>
      <span className={`ttab-coin coin-${instrument.symbol.toLowerCase()}`} aria-hidden>
        {GLYPH[instrument.symbol] ?? instrument.symbol.slice(0, 1)}
      </span>
      <span className="ttab-name">{instrument.symbol}</span>
      <span className="ttab-px num">{mark ? price(mark.raw, instrument.priceScale, { places: instrument.symbol === "BTC" ? 0 : 2 }) : ""}</span>
      {riding && <span className="ttab-live" aria-label="You have a bet here" />}
    </Link>
  );
}

/** The tables, one per market, each with its live price. */
function Tables({ route }: { route: Route }) {
  const instruments = useInstruments().filter((item) => !item.terminal);
  const positions = useAccount()?.positions ?? [];
  return (
    <nav className="ttabs" aria-label="Tables">
      {instruments.map((instrument) => (
        <TableTab
          key={instrument.id}
          instrument={instrument}
          current={route.view === "table" && route.coin === instrument.symbol}
          riding={positions.some((position) => position.instrumentId === instrument.id)}
        />
      ))}
    </nav>
  );
}

export function TopBar({ route }: { route: Route }) {
  return (
    <header className="topbar">
      <div className="topbar-inner">
        <Wordmark />
        <nav className="nav" aria-label="Primary">
          {NAV.map((item) => (
            <Link key={item.view} href={item.href} aria-current={route.view === item.view ? "page" : undefined}>
              {item.label}
            </Link>
          ))}
        </nav>
        <span className="topbar-spacer" />
        <Tables route={route} />
        <span className="topbar-spacer" />
        <SoundToggle />
        <Cage />
      </div>
    </header>
  );
}

export function TabBar({ route }: { route: Route }) {
  const live = useAccount()?.positions.length ?? 0;
  return (
    <nav className="tabbar" aria-label="Primary">
      {NAV.map((item) => (
        <Link key={item.view} href={item.href} className="tabbar-item" aria-current={route.view === item.view ? "page" : undefined}>
          <span className="tabbar-icon">
            <TabIcon view={item.view} />
            {item.view === "bets" && live > 0 && <span className="tabbar-badge num">{live}</span>}
          </span>
          <span>{item.label}</span>
        </Link>
      ))}
    </nav>
  );
}

function TabIcon({ view }: { view: Route["view"] }) {
  const common = { width: 22, height: 22, viewBox: "0 0 22 22", fill: "none", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  switch (view) {
    case "table":
      return (
        <svg {...common} aria-hidden>
          <circle cx="11" cy="11" r="8" />
          <circle cx="11" cy="11" r="4.5" strokeDasharray="2.2 2.2" />
          <circle cx="11" cy="11" r="1.2" fill="currentColor" />
        </svg>
      );
    case "bets":
      return (
        <svg {...common} aria-hidden>
          <rect x="4" y="3.5" width="14" height="15" rx="2" />
          <path d="M7.5 8h7M7.5 11.5h7M7.5 15h4" />
        </svg>
      );
    case "wallet":
      return (
        <svg {...common} aria-hidden>
          <rect x="3" y="5.5" width="16" height="12" rx="2" />
          <path d="M3 9h16M14.5 13h1.5" />
        </svg>
      );
  }
}
